import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import axios from "../../../../../axios";
import { Popup } from "semantic-ui-react";
import { Eye, RefreshCw } from "lucide-react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Button,
  Avatar,
  Tooltip,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import Table from "react-bootstrap/Table";

import ActiveSchoolPopUp from "./ActiveSchoolPopUp";
import UnblockScreen from "./UnblockScreen";
import { IP } from "./../../../../../internalServer";

/* ---------- Helpers ---------- */

const resolveAvatar = (student_id) => {
  if (student_id?.profile_picture) {
    return IP + student_id.profile_picture.replace(/^\./, "");
  }
  if (student_id?.gender === "M") {
    return student_id?.avatarProfile
      ? `/images/Boys/${student_id.avatarProfile}.webp`
      : "/images/Student_boy_pic.png";
  }
  if (student_id?.gender === "F") {
    return student_id?.avatarProfile
      ? `/images/Girls/${student_id.avatarProfile}.webp`
      : "/images/Student_girl_pic.png";
  }
  return "/images/default image.png";
};

const fallbackAvatar = (student_id) => {
  if (student_id?.gender === "M") {
    return student_id?.avatarProfile
      ? `/images/Boys/${student_id.avatarProfile}.webp`
      : "/images/Student_boy_pic.png";
  }
  if (student_id?.gender === "F") {
    return student_id?.avatarProfile
      ? `/images/Girls/${student_id.avatarProfile}.webp`
      : "/images/Student_girl_pic.png";
  }
  return "/images/default image.png";
};

/* ---------- Status chip ---------- */

const StatusChip = ({ student }) => {
  const status = student?.currentStatus;
  const unblockCount = student?.unblockCount || 0;

  if (status === "Blocked") {
    return (
      <Chip
        label="Blocked"
        size="small"
        sx={{
          bgcolor: "#fee2e2",
          color: "#b91c1c",
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          borderRadius: 1.5,
        }}
      />
    );
  }
  if (status === "Submitted") {
    return (
      <Chip
        label="Submitted"
        size="small"
        sx={{
          bgcolor: "#dcfce7",
          color: "#15803d",
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          borderRadius: 1.5,
        }}
      />
    );
  }
  if (unblockCount > 0) {
    return (
      <Chip
        label="Unblocked"
        size="small"
        sx={{
          bgcolor: "#fef3c7",
          color: "#b45309",
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          borderRadius: 1.5,
        }}
      />
    );
  }
  return (
    <Chip
      label={status || "Pending"}
      size="small"
      sx={{
        bgcolor: "#f3f4f6",
        color: "#6b7280",
        fontWeight: 600,
        fontSize: 11,
        height: 24,
        borderRadius: 1.5,
      }}
    />
  );
};

/* ---------- Main ---------- */

const PublishedStudentScr = ({ leftSideBarSize }) => {
  let isFromDcm = localStorage.getItem("from");
  let supplement_id = JSON.parse(localStorage.getItem("supplementId"));

  const { state } = useLocation();

  const [assessment, setAssessment] = useState();
  const [studentAssesment, setStudentAssesment] = useState([]);
  const [clickedAction, setClickedAction] = useState({});
  const [showUnblock, setShowUnblock] = useState(false);
  const [isGettingData, setIsGettingData] = useState(true);

  useEffect(() => {
    const getAssessment = async () => {
      try {
        let res = await axios.get(
          `/api/v1/assessment/get/${state.assesmentId}`,
        );
        res && setAssessment(res.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    getAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStudentData = async () => {
    try {
      setStudentAssesment([]);
      let response = await axios.get(
        `/api/v1/assessment/studentAssessment/getByAssessmentId/${state.assesmentId}?status=pending`,
      );
      setStudentAssesment(response?.data.data);
      setIsGettingData(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsGettingData(false);
    }
  };

  useEffect(() => {
    fetchStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backTo =
    localStorage.getItem("Header") === "Content Management"
      ? "/contentMangment"
      : isFromDcm === "DCM"
        ? "/DcmTimeTable"
        : isFromDcm === "Summary"
          ? `/ViewSummary`
          : "/publishedAssessment";

  const isAdaptive = state?.assessment?.isAdaptive;
  const isAutoEval = state?.assessment?.autoEvalEnabled;

  return (
    <Box
      className={`${leftSideBarSize === 70 ? "expand" : "collapasess"} container-fluid px-4 py-3`}
      sx={{ bgcolor: "#f7f8fa", minHeight: "100vh" }}
    >
      {/* ───────── Compact top bar ───────── */}
      <Paper
        elevation={0}
        sx={{
          px: 2.5,
          py: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Left — back + title + meta */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <NavLink
            to={backTo}
            state={{ supplement_id: supplement_id }}
            style={{ textDecoration: "none" }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f3f4f6",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all .15s",
                "&:hover": { bgcolor: "#e5e7eb" },
              }}
            >
              <ArrowBackIosNewRoundedIcon
                sx={{ fontSize: 15, color: "#374151" }}
              />
            </Box>
          </NavLink>

          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography fontWeight={700} fontSize={15} color="#111827" noWrap>
                {assessment?.name || "Loading…"}
              </Typography>

              <Chip
                label="PUBLISHED"
                size="small"
                sx={{
                  height: 20,
                  bgcolor: "#dcfce7",
                  color: "#15803d",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: 0.5,
                }}
              />

              {isAdaptive && (
                <Chip
                  icon={
                    <TrendingUpRoundedIcon
                      sx={{ fontSize: "14px !important" }}
                    />
                  }
                  label="ADAPTIVE"
                  size="small"
                  sx={{
                    height: 20,
                    bgcolor: "#fef3c7",
                    color: "#b45309",
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: 0.5,
                    "& .MuiChip-icon": { color: "#b45309" },
                  }}
                />
              )}

              {isAutoEval && (
                <Tooltip
                  arrow
                  title="Marks were generated automatically by the system"
                >
                  <Chip
                    icon={
                      <AutoAwesomeRoundedIcon
                        sx={{ fontSize: "14px !important" }}
                      />
                    }
                    label="AUTO EVALUATED"
                    size="small"
                    sx={{
                      height: 20,
                      bgcolor: "#e0e7ff",
                      color: "#3730a3",
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: 0.5,
                      "& .MuiChip-icon": { color: "#3730a3" },
                    }}
                  />
                </Tooltip>
              )}
            </Stack>

            <Typography
              fontSize={11}
              color="text.secondary"
              noWrap
              sx={{ mt: 0.2 }}
            >
              Dashboard / Assessment /{" "}
              <span style={{ color: "#4b5563", fontWeight: 500 }}>
                {assessment?.name}
              </span>
            </Typography>
          </Box>
        </Stack>

        {/* Right — refresh + view */}
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={fetchStudentData}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              px: 2,
              py: 0.75,
              borderColor: "#e5e7eb",
              color: "#374151",
              "&:hover": { borderColor: "#d1d5db", bgcolor: "#f9fafb" },
            }}
          >
            Refresh
          </Button>

          <NavLink
            to="/PublishedQList"
            state={{ assessmentId: state.assesmentId }}
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<VisibilityRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                px: 2,
                py: 0.75,
                boxShadow: "0 3px 10px rgba(37,99,235,.25)",
                "&:hover": {
                  background: "linear-gradient(135deg,#1d4ed8,#1e40af)",
                  boxShadow: "0 5px 14px rgba(37,99,235,.35)",
                },
              }}
            >
              View Assessment
            </Button>
          </NavLink>
        </Stack>
      </Paper>

      {/* ───────── Table card ───────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Card header — students count */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={700} fontSize={14} color="#111827">
              Students
            </Typography>
            <Chip
              label={studentAssesment.length}
              size="small"
              sx={{
                height: 20,
                minWidth: 24,
                bgcolor: "#eef2ff",
                color: "#1d4ed8",
                fontWeight: 700,
                fontSize: 11,
              }}
            />
            <Typography fontSize={11} color="text.secondary">
              pending only
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ borderTop: "1px solid #f3f4f6" }} />

        {/* Table */}
        {studentAssesment.length > 0 ? (
          <Box sx={{ maxHeight: 450, overflowY: "auto" }}>
            <Table
              borderless
              hover
              className="mb-0 align-middle h-full"
              style={{
                textAlign: "center",
                borderCollapse: "separate",
                borderSpacing: 0,
                marginBottom: 0,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Name",
                    "Roll No",
                    "Class",
                    "Division",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                        backgroundColor: "#f9fafb",
                        color: "#6b7280",
                        fontWeight: 600,
                        fontSize: 12,
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                        padding: "14px 12px",
                        borderBottom: "1px solid #e5e7eb",
                        whiteSpace: "nowrap",
                        textAlign: h === "Name" ? "left" : "center",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody style={{ fontSize: 13, color: "#374151" }}>
                {studentAssesment.map((student, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f9fafb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {/* Name */}
                    <td style={{ textAlign: "left", padding: "12px 16px" }}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ minWidth: 180 }}
                      >
                        <Avatar
                          src={resolveAvatar(student?.student_id)}
                          alt={student?.student_id?.name}
                          imgProps={{
                            onError: (e) =>
                              (e.target.src = fallbackAvatar(
                                student?.student_id,
                              )),
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            border: "2px solid #eef2ff",
                            fontSize: 13,
                            fontWeight: 700,
                            bgcolor: "#e0e7ff",
                            color: "#1d4ed8",
                          }}
                        >
                          {student?.student_id?.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Typography
                          fontSize={13}
                          fontWeight={500}
                          color="#1f2937"
                          noWrap
                          sx={{
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {student?.student_id?.name}
                        </Typography>
                      </Stack>
                    </td>

                    {/* Roll no */}
                    <td style={{ padding: "12px 16px" }}>
                      <Typography fontSize={13} color="#374151">
                        {student?.student_id?.roll_number || "—"}
                      </Typography>
                    </td>

                    {/* Class */}
                    <td style={{ padding: "12px 16px" }}>
                      <Typography fontSize={13} color="#374151">
                        {student?.assessment_id?.standard?.name || "—"}
                      </Typography>
                    </td>

                    {/* Division */}
                    <td style={{ padding: "12px 16px" }}>
                      <Typography fontSize={13} color="#374151">
                        {student?.assessment_id?.section?.name || "—"}
                      </Typography>
                    </td>

                    {/* Status */}
                    <td style={{ padding: "12px 16px" }}>
                      <StatusChip student={student} />
                    </td>

                    {/* Action */}
                    <td style={{ padding: "12px 16px" }}>
                      {student?.currentStatus === "Blocked" ? (
                        <Popup
                          trigger={
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "#6b7280",
                                transition: "all .15s",
                                "&:hover": {
                                  bgcolor: "#f3f4f6",
                                  color: "#374151",
                                },
                              }}
                            >
                              <MoreVertRoundedIcon sx={{ fontSize: 20 }} />
                            </Box>
                          }
                          content={
                            <div
                              className="hw_popup_box_size"
                              onClick={() => setClickedAction(student)}
                            >
                              <ActiveSchoolPopUp
                                setShowUnblock={setShowUnblock}
                              />
                            </div>
                          }
                          on="click"
                          hideOnScroll
                          position="bottom right"
                        />
                      ) : (
                        <Typography fontSize={13} color="#d1d5db">
                          —
                        </Typography>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        ) : isGettingData ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
            }}
          >
            <Stack alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#2563eb",
                  animation: "spin 0.8s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography fontSize={12} color="text.secondary">
                Loading students…
              </Typography>
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
            }}
          >
            <Stack alignItems="center" spacing={0.75}>
              <InboxRoundedIcon sx={{ fontSize: 42, color: "#d1d5db" }} />
              <Typography fontWeight={600} fontSize={14} color="#374151">
                No students to display
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                All pending students will appear here.
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>

      {showUnblock && (
        <UnblockScreen
          clickedAction={clickedAction}
          setShowUnblock={setShowUnblock}
          assesmentId={state.assesmentId}
          fetchStudentData={fetchStudentData}
        />
      )}
    </Box>
  );
};

export default PublishedStudentScr;
